import { CSSProperties, FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { clientConfig } from '@/config/clientConfig';
import { Icon } from '@/shared/uikit/Icon';
import { files as fileUtils } from '@/shared/utils/files';
import { numbers } from '@/shared/utils/numbers';
import { useModalsStore } from '@/widgets/modals/modals.store';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { FileRenderer } from './FileRenderer';
import { getFileInfo, printPreview } from './helpers';
import {
  ButtonWrapper,
  ChooseLanguageStyled,
  Header,
  HeaderContent,
  IconWrapper,
  IconWrapperText,
  InnerWrapper,
  Row,
  SmallText,
  Title,
  TitleWrapper,
  Wrapper,
} from './styled';
import { FilePreviewData, FilePreviewProps } from './types';
import { useContainerWidth } from './useContainerWidth';

const DESKTOP_PADDINGS = 184;
const TABLET_PADDINGS = 88;
const MOBILE_PADDINGS = -4;
const SIDEBAR_WIDTH = 200;
const TABLET_WIDTH = 991;
const DESKTOP_WIDTH = 1360;

export const FilePreview: FC<FilePreviewProps> = ({
  supportedLanguages,
  initLanguage,
  hidePrint,
  onLanguageChange,
  onRemove,
  onAttach,
  onEditComment,
  ...props
}) => {
  const { t } = useTranslation('common');
  const { t: tm } = useTranslation('widgets/modals', { keyPrefix: 'filePreview' });
  const modals = useModalsStore();
  const [headerHidden, setHeaderHidden] = useState(false);
  const [language, setLanguage] = useState(initLanguage);
  const [selectedFile, setSelectedFile] = useState<FilePreviewData>(props);
  const fileRendererRef = useRef<HTMLDivElement>(null);

  const { containerRef, containerWidth } = useContainerWidth();

  const isReadonlyMode = clientConfig.isExported;

  const onDownload = () => {
    if (props.mode === 'fileInfo') {
      fileUtils.downloadFileByUrl(props.fileInfo.url, props.fileInfo.name);
    } else {
      fileUtils.downloadFile(props.file, props.file.name);
    }
  };

  const thumbnailWidth = useMemo(() => {
    const isDesktop = containerWidth >= TABLET_WIDTH;
    if (!isDesktop) {
      let paddings = containerWidth >= DESKTOP_WIDTH ? DESKTOP_PADDINGS : TABLET_PADDINGS;
      if (containerWidth < TABLET_WIDTH) {
        paddings = MOBILE_PADDINGS;
      }
      return Math.floor((containerWidth - paddings) / 6);
    }
    return SIDEBAR_WIDTH;
  }, [containerWidth]);

  const selectedFileInfo = useMemo(() => {
    return getFileInfo(selectedFile);
  }, [selectedFile]);

  const handleRemove = () => {
    if (!onRemove) return;

    modals.openModal({
      type: 'ConfirmModal',
      variant: 'delete',
      title: tm('actions.removeConfirm'),
      onConfirm: onRemove,
    });
  };

  return (
    <Wrapper
      onClick={(e) => e.stopPropagation()}
      ref={containerRef}
      style={
        {
          '--thumbnailWidth': thumbnailWidth ? `${thumbnailWidth}px` : null,
        } as CSSProperties
      }
    >
      <InnerWrapper>
        <Header>
          <ButtonWrapper>
            <IconWrapper onClick={modals.closeModal}>
              <Icon name="arrow-left" size="s20" />
              <IconWrapperText>{t('back')}</IconWrapperText>
            </IconWrapper>
          </ButtonWrapper>
          <HeaderContent>
            <TitleWrapper>
              <Title>{selectedFileInfo.fileData.name}</Title>
              {selectedFileInfo.fileData.size && (
                <SmallText>
                  ({numbers.formatUnits(selectedFileInfo.fileData.size, 'kilobyte')})
                </SmallText>
              )}
            </TitleWrapper>
            <Row $hidden={headerHidden}>
              {!isReadonlyMode && onRemove && (
                <IconWrapper onClick={handleRemove}>
                  <Icon name="trash" />
                  <IconWrapperText>{tm('actions.remove')}</IconWrapperText>
                </IconWrapper>
              )}
              <IconWrapper onClick={onDownload}>
                <Icon name="download-simple" />
                <span>{t('download')}</span>
              </IconWrapper>
              {!isReadonlyMode && onEditComment && (
                <IconWrapper onClick={onEditComment}>
                  <Icon name="chat-text" />
                  <IconWrapperText>{tm('actions.comment')}</IconWrapperText>
                </IconWrapper>
              )}
              {!isReadonlyMode && onAttach && (
                <IconWrapper onClick={onAttach}>
                  <Icon name="paperclip" />
                  <IconWrapperText>{tm('actions.attach')}</IconWrapperText>
                </IconWrapper>
              )}
              {!hidePrint && (
                <IconWrapper
                  onClick={() => {
                    printPreview(props, fileRendererRef.current?.innerHTML);
                  }}
                >
                  <Icon name="printer" />
                  <IconWrapperText>{t('print')}</IconWrapperText>
                </IconWrapper>
              )}
              {supportedLanguages?.length && (
                <ChooseLanguageStyled
                  options={supportedLanguages}
                  value={language}
                  onChange={async (lang) => {
                    const data = await onLanguageChange?.(lang);
                    if (data) {
                      setLanguage(lang);
                      setSelectedFile(data);
                    }
                  }}
                />
              )}
            </Row>
          </HeaderContent>
        </Header>
        <FileRenderer
          ref={fileRendererRef}
          thumbnailWidth={thumbnailWidth}
          containerWidth={containerWidth}
          fileData={selectedFile}
          headerHidden={headerHidden}
          setHeaderHidden={setHeaderHidden}
        />
      </InnerWrapper>
    </Wrapper>
  );
};
